import { Check, Moon, Palette, Sun, SunMoon } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Menu } from '@unistocks/components/menu/menu';
import { t } from '@unistocks/localization';
import { useProfileUpdateSettingsMutation } from '@unistocks/proxy/axios-client/ProfileQuery';
import { useActiveColorScheme } from '@unistocks/shared/hooks/use-active-color-scheme';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { useSettingValue } from '@unistocks/shared/hooks/use-setting-value';
import { UnistocksSettings } from '@unistocks/shared/settings';
import { SelectOption } from '@unistocks/types';
import React, { PropsWithChildren, useState } from 'react';
import { ColorSchemeName } from 'react-native';
import { Spinner } from 'tamagui';

type ThemeValue = 'light' | 'dark' | null;

export type ThemeSelectProps = PropsWithChildren;

/**
 * Selects the current theme of the application and saves it to the user's profile.
 */
export const ThemeSelect: React.FC<ThemeSelectProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const themeSetting = useSettingValue<ThemeValue>(UnistocksSettings.PreferredTheme) ?? null;
  const { setActiveColorScheme } = useActiveColorScheme();
  const [open, setOpen] = useState(false);

  const themes: SelectOption<ThemeValue>[] = [
    {
      key: t('::Theme:Auto'),
      value: null,
      icon: <SunMoon color={'$bodyFont'} />
    },
    {
      key: t('::Theme:Light'),
      value: 'light',
      icon: <Sun color={'$yellow8'} />
    },
    {
      key: t('::Theme:Dark'),
      value: 'dark',
      icon: <Moon color={'$gray8'} />
    }
  ];

  const { mutateAsync, isPending, variables } = useProfileUpdateSettingsMutation({
    onSuccess: (_, parameters) => {
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
      setActiveColorScheme(
        parameters.settings[UnistocksSettings.PreferredTheme] as ColorSchemeName
      );
    }
  });

  const pendingTheme = (variables?.settings[UnistocksSettings.PreferredTheme] ??
    null) as ThemeValue;

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      trigger={children}
      headerTitle={t('::SelectWhat', { 0: t('::Settings:Theme').toLowerCase() })}
      headerIcon={
        <Palette $theme-dark={{ color: '$yellow9' }} $theme-light={{ color: '$yellow8' }} />
      }
    >
      {themes.map(item => {
        const isSelected = item.value === (themeSetting ?? null);
        const isSaving = isPending && item.value === pendingTheme;

        return (
          <Menu.Item
            key={item.key}
            closeOnPress={false}
            disabled={isPending}
            icon={item.icon}
            iconAfter={
              isSaving ? (
                <Spinner color={'$color'} size={'small'} />
              ) : isSelected ? (
                <Check color={'$success'} />
              ) : undefined
            }
            title={item.title ?? item.key}
            onPress={() =>
              mutateAsync({
                settings: {
                  [UnistocksSettings.PreferredTheme]: item.value as string
                }
              })
            }
          />
        );
      })}
    </Menu>
  );
};

ThemeSelect.displayName = 'ThemeSelect';
